/**
 * DecisionTreeWizard - Interactive HTS Classification Wizard
 *
 * This component renders an interactive decision tree for assembly-level
 * HTS classification. Users navigate through questions to reach a final
 * classification result.
 *
 * Features:
 * - Interactive question/answer navigation
 * - Back button to revisit previous questions
 * - Final classification display with rationale
 * - Integration with AssemblyTariffCalculator
 */

import { useState, useCallback } from 'react';
import { FiChevronRight, FiChevronLeft, FiRefreshCw, FiHelpCircle, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { IoCubeOutline } from 'react-icons/io5';

export default function DecisionTreeWizard({
    treeData,
    onClassificationComplete,
    materialSummary = {},
    savedClassification = null,  // If provided, skip wizard and show this result
}) {
    // State for wizard navigation
    const [currentNode, setCurrentNode] = useState(treeData);
    const [history, setHistory] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    // Track if we're showing saved classification or actively using wizard
    const [showingSaved, setShowingSaved] = useState(!!savedClassification);

    // Navigate to next node
    const handleContinue = useCallback(() => {
        if (!selectedAnswer) return;

        const answers = currentNode.answers || [];
        const selectedIndex = answers.findIndex(a => a.text === selectedAnswer);

        if (selectedIndex === -1) return;

        const nextNode = answers[selectedIndex].next;

        // Push current state to history
        setHistory(prev => [...prev, {
            question: currentNode.text,
            answer: selectedAnswer,
            node: currentNode
        }]);

        // Move to next node
        setCurrentNode(nextNode);
        setSelectedAnswer(null);

        // If this is a result node, notify parent
        if (nextNode.type === 'result' && onClassificationComplete) {
            onClassificationComplete({
                htsCode: nextNode.code,
                dutyRate: nextNode.generalDutyRatePercent || 0,
                rationale: nextNode.rationale || '',
                notes: nextNode.notes || '',
                sec301Applies: nextNode.sec301Applies || false,
                sec301List: nextNode.sec301List || '',
                sec232Applies: nextNode.sec232Applies || false,
            });
        }
    }, [currentNode, selectedAnswer, onClassificationComplete]);

    // Navigate back
    const handleBack = useCallback(() => {
        if (history.length === 0) return;

        const newHistory = [...history];
        const previousState = newHistory.pop();

        setHistory(newHistory);
        setCurrentNode(previousState.node);
        setSelectedAnswer(previousState.answer);
    }, [history]);

    // Reset wizard
    const handleReset = useCallback(() => {
        setCurrentNode(treeData);
        setHistory([]);
        setSelectedAnswer(null);
    }, [treeData]);

    // Handler to start reclassification (go through wizard again)
    const handleReclassify = useCallback(() => {
        setShowingSaved(false);
        setCurrentNode(treeData);
        setHistory([]);
        setSelectedAnswer(null);
    }, [treeData]);

    // If no tree data, show placeholder
    if (!treeData) {
        return (
            <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-8">
                <div className="text-center text-gray-500">
                    <FiHelpCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No Decision Tree Available</p>
                    <p className="text-sm mt-2">Run assembly analysis to generate the classification wizard.</p>
                </div>
            </div>
        );
    }

    const isQuestion = !showingSaved && currentNode.type === 'question';
    const isResult = !showingSaved && currentNode.type === 'result';
    const isShowingSavedResult = showingSaved && savedClassification;

    return (
        <div className="bg-white rounded-xl shadow-lg border-[3px] border-[#1e5045] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1e5045] to-[#225953] px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <IoCubeOutline className="w-6 h-6" />
                    Assembly HTS Classification {isShowingSavedResult ? '(Saved)' : 'Wizard'}
                </h2>
                <p className="text-green-100 text-sm mt-1">
                    Answer the questions below to determine the correct HTS code for the entire assembly
                </p>
            </div>

            {/* Progress indicator */}
            {history.length > 0 && (
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="font-medium">Progress:</span>
                        <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium">
                            {history.length} question(s) answered
                        </span>
                    </div>
                </div>
            )}

            {/* Content Area */}
            <div className="p-6">
                {/* Saved Classification Mode */}
                {isShowingSavedResult && (
                    <>
                        {/* Final Classification Banner */}
                        <div className="bg-gradient-to-r from-[#1e5045] to-[#225953] rounded-xl p-6 mb-6">
                            <div className="text-white text-sm font-medium opacity-90 mb-2">
                                SAVED CLASSIFICATION
                            </div>
                            <div className="text-[#f26522] text-3xl font-bold font-mono mb-3">
                                {savedClassification.htsCode}
                            </div>
                            <div className="text-white text-lg font-semibold">
                                General Duty Rate: {savedClassification.dutyRate || 0}%
                            </div>

                            {/* Section 301/232 Indicators */}
                            <div className="flex gap-4 mt-4">
                                {savedClassification.sec301Applies && (
                                    <div className="flex items-center gap-2 bg-amber-500/20 px-3 py-1.5 rounded-lg">
                                        <FiAlertTriangle className="w-4 h-4 text-amber-300" />
                                        <span className="text-amber-100 text-sm font-medium">
                                            Section 301 ({savedClassification.sec301List || 'China'})
                                        </span>
                                    </div>
                                )}
                                {savedClassification.sec232Applies && (
                                    <div className="flex items-center gap-2 bg-red-500/20 px-3 py-1.5 rounded-lg">
                                        <FiAlertTriangle className="w-4 h-4 text-red-300" />
                                        <span className="text-red-100 text-sm font-medium">
                                            Section 232 Applies
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Classification Rationale */}
                        {savedClassification.rationale && (
                            <div className="mb-6">
                                <details className="group" open>
                                    <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                        <span className="font-semibold text-gray-900 flex items-center gap-2">
                                            <FiCheckCircle className="w-5 h-5 text-green-600" />
                                            Classification Rationale
                                        </span>
                                    </summary>
                                    <div className="p-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
                                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                            {savedClassification.rationale}
                                        </p>
                                    </div>
                                </details>
                            </div>
                        )}

                        {/* Reclassify Button */}
                        <div className="flex justify-center pt-4 border-t border-gray-200">
                            <button
                                onClick={handleReclassify}
                                className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-all border border-gray-300"
                            >
                                <FiRefreshCw className="w-5 h-5" />
                                Reclassify Assembly
                            </button>
                        </div>
                    </>
                )}

                {/* Question Mode */}
                {isQuestion && (
                    <>
                        {/* Question Box */}
                        <div className="bg-[#f6f4ed] rounded-lg p-5 border-l-4 border-[#f26522] mb-6">
                            <div className="flex items-start gap-3">
                                <FiHelpCircle className="w-5 h-5 text-[#f26522] mt-0.5 flex-shrink-0" />
                                <p className="text-[#1e5045] text-lg font-semibold leading-relaxed">
                                    {currentNode.text}
                                </p>
                            </div>
                        </div>

                        {/* Answer Options */}
                        <div className="space-y-3 mb-6">
                            {(currentNode.answers || []).map((answer, idx) => (
                                <label
                                    key={idx}
                                    className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all
                                        ${selectedAnswer === answer.text
                                            ? 'border-[#1e5045] bg-green-50'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="wizard-answer"
                                        value={answer.text}
                                        checked={selectedAnswer === answer.text}
                                        onChange={() => setSelectedAnswer(answer.text)}
                                        className="w-5 h-5 text-[#1e5045] focus:ring-[#1e5045] focus:ring-offset-0"
                                    />
                                    <span className={`text-gray-800 ${selectedAnswer === answer.text ? 'font-medium' : ''}`}>
                                        {answer.text}
                                    </span>
                                </label>
                            ))}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                            <button
                                onClick={handleBack}
                                disabled={history.length === 0}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all
                                    ${history.length === 0
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                <FiChevronLeft className="w-5 h-5" />
                                Back
                            </button>

                            <button
                                onClick={handleContinue}
                                disabled={!selectedAnswer}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold transition-all
                                    ${!selectedAnswer
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-[#1e5045] text-white hover:bg-[#163a33] active:bg-[#0f2a25]'
                                    }`}
                            >
                                Continue
                                <FiChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </>
                )}

                {/* Result Mode */}
                {isResult && (
                    <>
                        {/* Final Classification Banner */}
                        <div className="bg-gradient-to-r from-[#1e5045] to-[#225953] rounded-xl p-6 mb-6">
                            <div className="text-white text-sm font-medium opacity-90 mb-2">
                                FINAL CLASSIFICATION
                            </div>
                            <div className="text-[#f26522] text-3xl font-bold font-mono mb-3">
                                {currentNode.code}
                            </div>
                            <div className="text-white text-lg font-semibold">
                                General Duty Rate: {currentNode.generalDutyRatePercent || 0}%
                            </div>

                            {/* Section 301/232 Indicators */}
                            <div className="flex gap-4 mt-4">
                                {currentNode.sec301Applies && (
                                    <div className="flex items-center gap-2 bg-amber-500/20 px-3 py-1.5 rounded-lg">
                                        <FiAlertTriangle className="w-4 h-4 text-amber-300" />
                                        <span className="text-amber-100 text-sm font-medium">
                                            Section 301 ({currentNode.sec301List || 'China'})
                                        </span>
                                    </div>
                                )}
                                {currentNode.sec232Applies && (
                                    <div className="flex items-center gap-2 bg-red-500/20 px-3 py-1.5 rounded-lg">
                                        <FiAlertTriangle className="w-4 h-4 text-red-300" />
                                        <span className="text-red-100 text-sm font-medium">
                                            Section 232 Applies
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Classification Rationale */}
                        {currentNode.rationale && (
                            <div className="mb-6">
                                <details className="group" open>
                                    <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                        <span className="font-semibold text-gray-900 flex items-center gap-2">
                                            <FiCheckCircle className="w-5 h-5 text-green-600" />
                                            Classification Rationale
                                        </span>
                                        <span className="text-gray-500 text-sm group-open:hidden">Click to expand</span>
                                    </summary>
                                    <div className="p-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
                                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                            {currentNode.rationale}
                                        </p>
                                    </div>
                                </details>
                            </div>
                        )}

                        {/* Notes/Warnings */}
                        {currentNode.notes && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                                <div className="flex items-start gap-3">
                                    <FiAlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium text-amber-800 mb-1">Important Notes</p>
                                        <p className="text-amber-700 text-sm">{currentNode.notes}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Path Summary */}
                        {history.length > 0 && (
                            <div className="mb-6">
                                <details className="group">
                                    <summary className="flex items-center justify-between cursor-pointer p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                                        <span className="font-medium text-gray-700">
                                            Classification Path ({history.length} steps)
                                        </span>
                                        <span className="text-gray-500 text-sm">Click to view</span>
                                    </summary>
                                    <div className="p-4 bg-gray-50 rounded-b-lg border-t border-gray-200 space-y-3">
                                        {history.map((step, idx) => (
                                            <div key={idx} className="text-sm">
                                                <p className="text-gray-500 mb-1">Q{idx + 1}: {step.question}</p>
                                                <p className="text-gray-900 font-medium pl-4">→ {step.answer}</p>
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            </div>
                        )}

                        {/* Start Over Button */}
                        <div className="pt-4 border-t border-gray-200">
                            <button
                                onClick={handleReset}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-all"
                            >
                                <FiRefreshCw className="w-5 h-5" />
                                Start Over
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
